/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useCurrentQueueItem } from "@packages/media";
import { useRequestUsersMeSettings } from "@packages/sdk";
import { memo, useCallback, useState } from "react";

import {
  constantSemanticColors,
  numericPercentages,
  radius,
  spacing,
  stroke,
  zIndices,
} from "../../../../../../global/emotion";
import { IconButton } from "../../../../../components/_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import { Toggle } from "../../../../../components/_base/_core/Toggle/Toggle.emotion";
import {
  ColorIcon,
  ModeIcon,
  ScrollIcon,
  TextSizeIcon,
} from "../../../../../components/_base/icons/Icon/GeneralIcons";
import { DropdownMenuItem } from "../../../../../components/_base/modals/DropdownMenuItem/DropdownMenuItem.emotion";
import { ModalDropdownMenu } from "../../../../../components/_base/modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenuHeader } from "../../../../../components/_base/modals/ModalDropdownMenuHeader/ModalDropdownMenuHeader.emotion";
import { ModalHeader } from "../../../../../components/_base/modals/ModalHeader/ModalHeader.emotion";
import { TabGroup } from "../../../../../components/blocks/other/TabGroup/TabGroup.emotion";
import { TabGroupItem } from "../../../../../components/blocks/other/TabGroupItem/TabGroupItem.emotion";
import { useIsDesktopViewport } from "../../../../../hooks/useIsDesktopViewport";
import { useThemeValues } from "../../../../../hooks/useThemeValues";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";

type FontSize = "small" | "medium" | "large";

const FONT_SIZE_OPTIONS: Array<{
  value: FontSize;
  icon: React.ComponentType;
  labelKey: string;
}> = [
  {
    value: "small",
    icon: TextSizeIcon.Small,
    labelKey: "transcript_options_dialog_text_size_small",
  },
  {
    value: "medium",
    icon: TextSizeIcon.Medium,
    labelKey: "transcript_options_dialog_text_size_medium",
  },
  {
    value: "large",
    icon: TextSizeIcon.Large,
    labelKey: "transcript_options_dialog_text_size_large",
  },
];

const toggleStyles: Record<string, Interpolation> = {
  container: {
    border: `${stroke.regular} solid ${constantSemanticColors.fixedLightenLowest}`,
    borderRadius: radius.m,
    display: "flex",
    flexDirection: "column",
    gap: spacing.l,
    padding: spacing.m,
    width: spacing.full,
  },
  toggleSettingContainer: {
    alignItems: "center",
    columnGap: spacing.m,
    display: "grid",
    gridTemplateAreas: '"icon text toggle"',
    gridTemplateColumns: "min-content 1fr min-content",
    gridTemplateRows: "1fr",
  },
  disabledOptionContainer: {
    cursor: "not-allowed",
    opacity: 0.5,
  },
  text: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    rowGap: spacing.t,
  },
};

const mobileStyles: Record<string, Interpolation> = {
  menuItemContainer: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: numericPercentages[100],
  },
  menuItem: {
    alignItems: "center",
    borderRadius: radius.m,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    height: "max-content",
    justifyContent: "center",
    minWidth: "revert",
    padding: `${spacing.m} 0`,
    rowGap: spacing.s,
  },
  menuHeader: {
    marginTop: spacing.m,
  },
  noHeaderMenuItem: {
    marginBottom: spacing.m,
    marginTop: spacing.xl,
  },
};

const settingsStyle: Interpolation = {
  opacity: 1,
  pointerEvents: "auto",
  position: "fixed",
  right: spacing.m,
  top: spacing.m,
  transition: "opacity 250ms ease-out",
  willChange: "opacity",
  zIndex: zIndices.maskOver,
  [cssMediaQueryMinWidthTablet]: {
    right: "80px",
    top: "80px",
  },
};

const desktopStyles: Record<string, Interpolation> = {
  item: {
    justifyContent: "flex-start",
  },
  text: {
    flexGrow: 1,
  },
};

const dropdownStyle: Interpolation = {
  zIndex: zIndices.modalOverTranscript,
};

const FontSizeOptions = ({
  fontSize,
  setFontSize,
}: {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => Promise<void>;
}) => {
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();

  if (isDesktop) {
    return (
      <>
        {FONT_SIZE_OPTIONS.map(({ value, icon: IconComponent, labelKey }) => (
          <DropdownMenuItem
            key={value}
            startIcon={<IconComponent />}
            selected={fontSize === value}
            onSelect={async (e) => {
              e.preventDefault();
              await setFontSize(value);
            }}
            css={desktopStyles.item}
            asChild
          >
            <div>
              <span css={desktopStyles.text}>{t(labelKey)}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </>
    );
  }

  return (
    <TabGroup
      type="single"
      value={fontSize}
      css={mobileStyles.menuItemContainer}
      onValueChange={setFontSize}
    >
      {FONT_SIZE_OPTIONS.map(({ value, icon: IconComponent, labelKey }) => (
        <TabGroupItem
          key={value}
          css={mobileStyles.menuItem}
          value={value}
          parentState={fontSize}
        >
          <>
            <IconComponent />
            <Text type="button" size="s">
              {t(labelKey)}
            </Text>
          </>
        </TabGroupItem>
      ))}
    </TabGroup>
  );
};

const AutoScrollSetting = ({
  autoScrollEnabled,
  setAutoScrollEnabled,
  isAutoScrollSupported,
}: {
  autoScrollEnabled: boolean;
  setAutoScrollEnabled: (enabled: boolean) => Promise<void>;
  isAutoScrollSupported: boolean;
}) => {
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const theme = useThemeValues();

  return (
    <div
      css={[
        toggleStyles.toggleSettingContainer,
        !isDesktop && mobileStyles.noHeaderMenuItem,
        !isAutoScrollSupported && toggleStyles.disabledOptionContainer,
      ]}
    >
      <ScrollIcon
        color={
          isAutoScrollSupported && isDesktop
            ? constantSemanticColors.onOverlayDarkMed
            : theme.semanticColors.primary
        }
      />
      <div css={toggleStyles.text}>
        <Text type="title" size="m">
          {t("tos_auto_scroll")}
        </Text>
        {!isAutoScrollSupported && (
          <Text type="detail" size="xl">
            {t("tos_auto_scroll_not_supported")}
          </Text>
        )}
      </div>
      {isAutoScrollSupported && (
        <Toggle
          pressed={autoScrollEnabled}
          onPressedChange={setAutoScrollEnabled}
        />
      )}
    </div>
  );
};

const ImmersiveSetting = ({
  immersive,
  setImmersive,
}: {
  immersive: boolean;
  setImmersive: (immersive: boolean) => Promise<void>;
}) => {
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();

  if (isDesktop) {
    return (
      <div css={toggleStyles.toggleSettingContainer}>
        <ModeIcon color={constantSemanticColors.onOverlayDarkMed} />
        <div css={toggleStyles.text}>
          <Text type="title" size="m">
            {t("text_on_screen_immersion_mode")}
          </Text>
          <Text type="detail" size="xl">
            {t("text_on_screen_immersion_mode_explanation")}
          </Text>
        </div>
        <Toggle pressed={immersive} onPressedChange={setImmersive} />
      </div>
    );
  }

  return (
    <>
      <ModalHeader css={mobileStyles.menuHeader}>
        {t("transcript_options_dialog_background")}
      </ModalHeader>
      <TabGroup
        type="single"
        value={immersive ? "immersive" : "default"}
        onValueChange={(value) => setImmersive(value === "immersive")}
        css={mobileStyles.menuItemContainer}
      >
        <TabGroupItem
          css={mobileStyles.menuItem}
          value="immersive"
          parentState={immersive ? "immersive" : "default"}
        >
          <>
            <ColorIcon />
            <Text type="button" size="s">
              {t("transcript_options_dialog_background_color")}
            </Text>
          </>
        </TabGroupItem>
        <TabGroupItem
          css={mobileStyles.menuItem}
          value="default"
          parentState={immersive ? "immersive" : "default"}
        >
          <>
            <ModeIcon />
            <Text type="button" size="s">
              {t("transcript_options_dialog_background_theme")}
            </Text>
          </>
        </TabGroupItem>
      </TabGroup>
    </>
  );
};

export const TextSettings = memo(() => {
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const { mutationPut: updateSettings, query: settingsQuery } =
    useRequestUsersMeSettings({});
  const { currentItem } = useCurrentQueueItem();
  const analytics = useAnalytics();
  const theme = useThemeValues();

  const settings = settingsQuery?.data?.global;
  const fontSize = settings?.tos_font_size as FontSize;
  const immersive = settings?.tos_bg_color === "content_color_hex";
  const autoScrollEnabled = settings?.tos_default_auto_scroll === "true";
  const isAutoScrollSupported =
    currentItem.selected_audio.transcript_type === "vtt";

  const menuTrigger = (
    <IconButton
      icon={<TextSizeIcon.Small />}
      variant={"transparentPrimary"}
      css={settingsStyle}
      onPointerDown={() => {
        analytics.track({
          event: HallowAnalyticsEvent.TappedTranscriptSettings,
          properties: {
            screen_name: "transcript",
            prayer: currentItem.selected_audio.prayer_id,
          },
        });
      }}
    />
  );

  const trackOptionTapped = useCallback(
    (option: string) => {
      analytics.track({
        event: HallowAnalyticsEvent.TappedTranscriptOption,
        properties: {
          option,
          prayer: currentItem.selected_audio.prayer_id,
        },
      });
    },
    [analytics, currentItem.selected_audio.prayer_id],
  );

  const setFontSize = useCallback(
    async (fontSize: FontSize) => {
      trackOptionTapped("text_size");
      analytics.track({
        event: HallowAnalyticsEvent.ChangedTranscriptTextSizeSetting,
        properties: {
          text_size: fontSize,
          prayer: currentItem.selected_audio.prayer_id,
        },
      });
      await updateSettings.mutateAsync({
        settings: [{ key: "tos_font_size", value: fontSize, scope: "global" }],
      });
    },
    [
      analytics,
      currentItem.selected_audio.prayer_id,
      trackOptionTapped,
      updateSettings,
    ],
  );

  const setImmersive = useCallback(
    async (immersive: boolean) => {
      trackOptionTapped("color");
      analytics.track({
        event: HallowAnalyticsEvent.ChangedTranscriptColorSetting,
        properties: {
          color: immersive ? "color" : "system_theme",
          prayer: currentItem.selected_audio.prayer_id,
        },
      });
      await updateSettings.mutateAsync({
        settings: [
          {
            key: "tos_bg_color",
            value: immersive ? "content_color_hex" : "system_theme",
            scope: "global",
          },
        ],
      });
    },
    [
      analytics,
      currentItem.selected_audio.prayer_id,
      trackOptionTapped,
      updateSettings,
    ],
  );

  const setAutoScrollEnabled = useCallback(
    async (autoScrollEnabled: boolean) => {
      trackOptionTapped("auto_scroll");
      analytics.track({
        event: HallowAnalyticsEvent.ChangedTranscriptAutoScrollSetting,
        properties: {
          enabled: autoScrollEnabled,
          prayer: currentItem.selected_audio.prayer_id,
        },
      });
      await updateSettings.mutateAsync({
        settings: [
          {
            key: "tos_default_auto_scroll",
            value: autoScrollEnabled.toString(),
            scope: "global",
          },
        ],
      });
    },
    [
      analytics,
      currentItem.selected_audio.prayer_id,
      trackOptionTapped,
      updateSettings,
    ],
  );

  return (
    <ModalDropdownMenu
      trigger={menuTrigger}
      open={open}
      onOpenChange={setOpen}
      modalProps={{
        overlayProps: {
          onClick: () => setOpen(false),
        },
        contentCSS: { color: theme.semanticColors.primary },
      }}
      dropdownMenuProps={{
        align: "end",
      }}
    >
      <ModalDropdownMenuHeader>
        {isDesktop
          ? t("text_on_screen_text_options")
          : t("transcript_options_dialog_text_size")}
      </ModalDropdownMenuHeader>
      <FontSizeOptions fontSize={fontSize} setFontSize={setFontSize} />
      <div css={isDesktop && toggleStyles.container}>
        <ImmersiveSetting immersive={immersive} setImmersive={setImmersive} />
        <AutoScrollSetting
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
          isAutoScrollSupported={isAutoScrollSupported}
        />
      </div>
    </ModalDropdownMenu>
  );
});

TextSettings.displayName = "TextSettings";
